import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme, styled } from '@mui/material/styles';
import { get, getUserIdFromToken, getUserRoleFromToken, post } from 'api';
import { PrimaryTab } from 'components/Tabs';
import avatarWhite from 'assets/images/defaultAvatar-white.svg';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import calendar from 'assets/icons/calendar-check-02.svg';
import { ErrorToast, SuccessToast } from 'utils/Toasts';
import { Edit } from '@mui/icons-material';
import EditMatchResultModal from 'components/EditResultModal';

const GroupTable = ({ level, filter }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const successToastRef = useRef();
  const errorToastRef = useRef();
  const token = getUserIdFromToken();
  const { id } = useParams();
  const [activeLevel, setActiveLevel] = useState('light');
  const [groups, setGroups] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [showTooltip, setShowTooltip] = useState({});
  const [groupMatches, setGroupMatches] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const isAdmin = getUserRoleFromToken() === 'admin' ? true : false;

  const LightTooltip = styled(({ className, ...props }) => (
    <Tooltip
      {...props}
      open={props.open}
      onOpen={props.onOpen}
      onClose={props.onClose}
      classes={{ popper: className }}
    />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.base.white,
      color: theme.palette.darkGrey[700],
      boxShadow:
        '0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)',
      fontSize: 12,
      fontFamily: 'Inter, sans-serif',
      padding: '8px 12px',
      borderRadius: '8px',
    },
  }));

  const fetchGroups = async () => {
    const response = await get(`/groups/list/${id}`);
    const data = await response.json();
    const filtered = data.filter((item) => item.type_name === filter.gender);
    setAllData(filtered);

    const levelsOrder = [
      'super light (d)',
      'light (d/c-)',
      'middle (c)',
      'power (c+/b-)',
      'hard (b)',
      'top (b+)',
    ];

    const availableLevels = filtered
      .map((item) => item.level_name.toLowerCase())
      .filter((level) => levelsOrder.some((order) => level.includes(order)));

    const fetchedLevel =
      availableLevels.length > 0
        ? levelsOrder.find((order) =>
            availableLevels.some((level) => level.includes(order)),
          )
        : 'light';

    setActiveLevel(level ? level.toLowerCase() : fetchedLevel);
  };

  useEffect(() => {
    fetchGroups();
  }, [id, token, filter.gender, level]);

  useEffect(() => {
    if (level) {
      setActiveLevel(level.toLowerCase());
    }
  }, [level]);

  useEffect(() => {
    const filteredGroups = allData.filter(
      (item) =>
        item.level_name.toLowerCase() === activeLevel &&
        item.group_name !== null,
    );
    setFilteredData(filteredGroups);
  }, [activeLevel, allData, filter]);

  const fetchGroupMatches = async () => {
    const response = await get(`/groups/matches/${id}`);
    const data = await response.json();

    setGroupMatches(data);
  };

  useEffect(() => {
    fetchGroupMatches();
  }, [id]);

  const handleGroupPlaceChange = async (newPlace, group_id, team_id) => {
    try {
      // Update the group place in the backend
      const response = await post(`/groups/update-place`, {
        group_id,
        team_id,
        group_place: newPlace,
      });

      const responseData = await response.json();

      if (response.ok) {
        successToastRef.current.showSuccess(responseData.msg);
        fetchGroups();
        fetchGroupMatches();
      } else {
        errorToastRef.current.showError(responseData.msg);
      }
    } catch (error) {
      console.error('Error updating group place:', error);
    }
  };

  const openEditModal = (match) => {
    setSelectedMatch(match);
    setIsModalOpen(true);
  };

  const closeEditModal = () => {
    setSelectedMatch(null);
    setIsModalOpen(false);
  };

  const matchMap = new Map();

  groupMatches.forEach((match) => {
    const key1 = `${match.team1_id}-${match.team2_id}`;
    const key2 = `${match.team2_id}-${match.team1_id}`;
    matchMap.set(key1, match);
    matchMap.set(key2, match);
  });

  const divideByGroups = useCallback(() => {
    const groups = {
      A: [],
      B: [],
      C: [],
      D: [],
      E: [],
      F: [],
      G: [],
      H: [],
      I: [],
      J: [],
      K: [],
      L: [],
      M: [],
      N: [],
      O: [],
      P: [],
      Q: [],
      R: [],
    };

    filteredData.forEach((item) => {
      groups[item.group_name].push(item);
    });

    setGroups(Object.values(groups));
  }, [filteredData]);

  useEffect(() => {
    if (filteredData.length > 0) {
      divideByGroups();
      setLoaded(true);
    }
  }, [filteredData, divideByGroups]);

  const groupSize5 = 5;
  const groupSize6 = 6;
  const groupSize7 = 7;

  const isWinner = (rez) => {
    if (rez === null) {
      return false;
    }
    const sets = rez.split(' ');
    let playerWins = 0;
    let opponentWins = 0;
    sets.forEach((set) => {
      const scores = set.split(':');
      const playerScore = parseInt(scores[0]);
      const opponentScore = parseInt(scores[1]);
      if (playerScore > opponentScore) {
        playerWins++;
      } else if (playerScore < opponentScore) {
        opponentWins++;
      }
    });
    return playerWins > opponentWins;
  };

  const updatedRez = (rez, index, i, group_name, matchDetails, match) => {
    // if (rez === null && matchDetails === null) {
    //   return '';
    // }

    if (matchDetails && !rez) {
      return (
        <Box
          component={'span'}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          <LightTooltip
            title={matchDetails}
            placement={'top'}
            open={showTooltip[`${group_name}-${index}-${i}`] || false}
            onOpen={() =>
              setShowTooltip((prevState) => ({
                ...prevState,
                [`${group_name}-${index}-${i}`]: true,
              }))
            }
            onClose={() =>
              setShowTooltip((prevState) => ({
                ...prevState,
                [`${group_name}-${index}-${i}`]: false,
              }))
            }
          >
            <Box
              component={'img'}
              src={calendar}
              marginLeft={'8px'}
              sx={{ cursor: 'pointer' }}
              onClick={() =>
                setShowTooltip((prevState) => ({
                  ...prevState,
                  [`${group_name}-${index}-${i}`]:
                    !prevState[`${group_name}-${index}-${i}`],
                }))
              }
              alt="calendar icon"
            />
          </LightTooltip>
          {isAdmin && match && (
            <Edit
              sx={{
                ml: '4px',
                fontSize: '16px',
                color: theme.palette.grey[500],
                cursor: 'pointer',
                '&:hover': {
                  color: theme.palette.primary.main,
                },
              }}
              onClick={() => openEditModal(match)}
            />
          )}
        </Box>
      );
    } else {
      const parts = rez?.split(' ');
      const regex = /\((\d+)\)/g;
      const superscriptParts = parts?.map((part) =>
        part.replace(regex, '<sup>$1</sup>'),
      );
      const superscriptRezultatas = superscriptParts?.join(' ');

      return (
        <Box
          component={'span'}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          <Typography
            variant="body5"
            fontWeight={isWinner(rez) ? 700 : 500}
            color={
              isWinner(rez)
                ? theme.palette.darkGrey[700]
                : theme.palette.darkGrey[600]
            }
            dangerouslySetInnerHTML={{ __html: superscriptRezultatas }}
          />
          {isAdmin && match && (
            <Edit
              sx={{
                ml: '4px',
                fontSize: '16px',
                color: theme.palette.grey[500],
                cursor: 'pointer',
                '&:hover': {
                  color: theme.palette.primary.main,
                },
              }}
              onClick={() => openEditModal(match)}
            />
          )}
        </Box>
      );
    }
  };

  const createCells = (i, groupLength, player, allPlayers, matchMap) => {
    const { points, group_name, group_place, group_id, team_id } = player;

    return (
      <>
        {allPlayers.map((opponent, index) => {
          if (index === i) {
            return (
              <Box
                key={`cell-${i}-${index}`}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  width: '126px',
                  boxSizing: 'border-box',
                  justifyContent: 'center',
                  borderLeft: `1px solid ${theme.palette.grey[500]}`,
                  backgroundColor: theme.palette.darkGrey[300],
                  padding: '20px 12px',
                  minHeight: '92px',
                }}
              />
            );
          } else if (index >= groupLength) {
            return null;
          } else {
            const matchKey = `${team_id}-${opponent.team_id}`;
            const match = matchMap.get(matchKey);
            const matchDetails =
              match && match.match_date && match.match_time
                ? `${match.match_date} ${match.match_time} (${match.match_court})`
                : null;
            const score = match
              ? team_id === match.team1_id
                ? match.score_team1
                : match.score_team2
              : '';

            return (
              <Box
                key={`cell-${i}-${index}`}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  width: '126px',
                  boxSizing: 'border-box',
                  justifyContent: 'center',
                  padding: '20px 12px',
                  minHeight: '92px',
                  borderLeft: `1px solid ${theme.palette.grey[500]}`,
                  alignItems: 'center',
                }}
              >
                <Typography variant="body4" fontWeight={600}>
                  {updatedRez(score, index, i, group_name, matchDetails, match)}
                </Typography>
              </Box>
            );
          }
        })}
        {/* Points cell */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            width: '126px',
            boxSizing: 'border-box',
            justifyContent: 'center',
            padding: '20px 16px',
            minHeight: '92px',
            borderLeft: `1px solid ${theme.palette.grey[500]}`,
            alignItems: 'center',
            backgroundColor: theme.palette.darkGrey[100],
          }}
        >
          <Typography
            variant="body4"
            fontWeight={600}
            color={theme.palette.darkGrey[700]}
          >
            {!points ? '0' : points}
          </Typography>
        </Box>

        {/* Group place cell */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            width: '126px',
            boxSizing: 'border-box',
            justifyContent: 'center',
            padding: '20px 16px',
            minHeight: '92px',
            borderLeft: `1px solid ${theme.palette.grey[500]}`,
            alignItems: 'center',
            backgroundColor: theme.palette.darkGrey[100],
          }}
        >
          {isAdmin ? (
            <input
              type="number"
              value={group_place || ''}
              onChange={(e) =>
                handleGroupPlaceChange(e.target.value, group_id, team_id)
              }
              style={{
                width: '50px',
                textAlign: 'center',
                fontSize: '14px',
                fontWeight: 600,
                color: theme.palette.darkGrey[700],
                border: `1px solid ${theme.palette.grey[500]}`,
                borderRadius: '4px',
                padding: '4px',
              }}
            />
          ) : (
            <Typography
              variant="body4"
              fontWeight={600}
              color={theme.palette.darkGrey[700]}
            >
              {!group_place ? '-' : group_place}
            </Typography>
          )}
        </Box>
      </>
    );
  };

  if (!loaded) {
    return null;
  }

  return (
    <Box>
      <SuccessToast ref={successToastRef} />
      <ErrorToast ref={errorToastRef} />
      <EditMatchResultModal
        open={isModalOpen}
        onClose={closeEditModal}
        match={selectedMatch}
        fetchGroups={fetchGroups}
        fetchGroupMatches={fetchGroupMatches}
        successToastRef={successToastRef}
        errorToastRef={errorToastRef}
        openedFrom="group"
      />
      <Box
        maxWidth={'700px'}
        marginBottom={'48px'}
        display={{ xs: 'flex', sm: 'inline-flex' }}
        justifyContent={'space-between'}
        border={`1px solid ${theme.palette.darkGrey[300]}`}
        borderRadius={'8px'}
        flexDirection={{ xs: 'column', sm: 'row' }}
      >
        {[
          'super light (d)',
          'light (d/c-)',
          'middle (c)',
          'power (c+/b-)',
          'hard (b)',
          'top (b+)',
        ].map((level) => {
          const hasLevel = allData.some(
            (item) => item.level_name.toLowerCase() === level,
          );

          return (
            hasLevel && (
              <PrimaryTab
                key={level}
                activeTab={activeLevel}
                setActiveTab={setActiveLevel}
                tabName={level}
                pageName={'groupTable'}
                upperCase={true}
              />
            )
          );
        })}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {groups.length > 0 &&
          groups.map((group, index) => {
            const groupNames = [
              'A',
              'B',
              'C',
              'D',
              'E',
              'F',
              'G',
              'H',
              'I',
              'J',
              'K',
              'L',
              'M',
              'N',
              'O',
              'P',
              'Q',
              'R',
            ];
            const groupName = groupNames[index] || '';

            if (group.length === 0) {
              return '';
            }

            const groupSize = group.length;
            const tableWidth = groupSize * 126 + 280 + 126 + 126 + 4;
            return (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  maxWidth: `${tableWidth}px`,
                  overflowX: 'auto',
                  marginBottom: '32px',
                  paddingBottom: '4px',
                  '&::-webkit-scrollbar': {
                    height: '6px',

                    backgroundColor: theme.palette.grey[200],
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: theme.palette.grey[400],
                    borderRadius: '8px',
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        width: '280px',
                        paddingLeft: '16px',
                      }}
                    >
                      <Typography
                        variant="body1"
                        fontWeight={700}
                        color={theme.palette.darkGrey[700]}
                      >
                        {t('group')} {groupName}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'flex-end',
                        width: 'auto',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          width: '126px',
                          justifyContent: 'center',
                          minHeight: '3rem',
                        }}
                      />
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          width: '126px',
                          justifyContent: 'center',
                          minHeight: '3rem',
                        }}
                      />
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          width: '126px',
                          justifyContent: 'center',
                          minHeight: '3rem',
                        }}
                      />
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          width: '126px',
                          justifyContent: 'center',
                          minHeight: '3rem',
                        }}
                      />
                      {group.length < groupSize5 ? (
                        ''
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            width: '126px',

                            justifyContent: 'center',
                            minHeight: '3rem',
                          }}
                        />
                      )}
                      {group.length < groupSize6 ? (
                        ''
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            width: '126px',

                            justifyContent: 'center',
                            minHeight: '3rem',
                          }}
                        />
                      )}
                      {group.length < groupSize7 ? (
                        ''
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            width: '126px',

                            justifyContent: 'center',
                            minHeight: '3rem',
                          }}
                        />
                      )}
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          width: '126px',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography
                          variant="body4"
                          fontWeight={500}
                          color={theme.palette.darkGrey[700]}
                        >
                          {t('points')}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          width: '126px',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <Typography
                          variant="body4"
                          fontWeight={500}
                          color={theme.palette.darkGrey[700]}
                        >
                          {t('place')}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      border: `2px solid ${theme.palette.darkGrey[400]}`,
                      borderRadius: '16px',
                      width: '100%',
                      overflow: 'hidden',
                    }}
                  >
                    {group.map((player, i) => {
                      const isLastItem = i === group.length - 1;
                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            width: '100%',
                            minHeight: '92px',
                            borderBottom: isLastItem
                              ? 'none'
                              : `1px solid ${theme.palette.grey[500]}`,
                          }}
                          key={i}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              width: '280px',
                              padding: '12px 16px',
                              minHeight: '92px',
                              alignItems: 'start',
                              justifyContent: 'flex-start',
                              gap: '4px',
                              backgroundColor: theme.palette.darkGrey[100],
                            }}
                          >
                            {/* First Player */}
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                width: '100%',
                              }}
                            >
                              <Box
                                sx={{
                                  width: '32px',
                                  height: '32px',
                                  flexShrink: 0,
                                }}
                              >
                                <Box
                                  component={'img'}
                                  src={
                                    player.player1_image
                                      ? `${process.env.REACT_APP_BACKEND_URL_BASE}/public/images/${player.player1_image}`
                                      : avatarWhite
                                  }
                                  alt="avatar"
                                  sx={{
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                  }}
                                />
                              </Box>
                              <Box
                                sx={{ marginLeft: '16px', maxWidth: '200px' }}
                              >
                                <Box
                                  component={'a'}
                                  sx={{
                                    cursor: 'pointer',
                                    textDecoration: 'none',
                                    '&:hover': {
                                      textDecoration: 'underline',
                                      textDecorationColor:
                                        theme.palette.darkGrey[700],
                                    },
                                  }}
                                  href={`${window.location.origin}/info/${player.player1_id}`}
                                >
                                  <Typography
                                    variant="body4"
                                    fontWeight={600}
                                    color={theme.palette.darkGrey[700]}
                                    sx={{
                                      whiteSpace: 'normal',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                    }}
                                  >
                                    {player.player1_name}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>

                            {/* Second Player */}
                            {player.player2_id && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  alignItems: 'center',
                                  width: '100%',
                                }}
                              >
                                <Box
                                  sx={{
                                    width: '32px',
                                    height: '32px',
                                    flexShrink: 0,
                                  }}
                                >
                                  <Box
                                    component={'img'}
                                    src={
                                      player.player2_image
                                        ? `${process.env.REACT_APP_BACKEND_URL_BASE}/public/images/${player.player2_image}`
                                        : avatarWhite
                                    }
                                    alt="avatar"
                                    sx={{
                                      width: '100%',
                                      height: '100%',
                                      borderRadius: '50%',
                                      objectFit: 'cover',
                                    }}
                                  />
                                </Box>
                                <Box
                                  sx={{ marginLeft: '16px', maxWidth: '200px' }}
                                >
                                  <Box
                                    component={'a'}
                                    sx={{
                                      cursor: 'pointer',
                                      textDecoration: 'none',
                                      '&:hover': {
                                        textDecoration: 'underline',
                                        textDecorationColor:
                                          theme.palette.darkGrey[700],
                                      },
                                    }}
                                    href={`${window.location.origin}/info/${player.player2_id}`}
                                  >
                                    <Typography
                                      variant="body4"
                                      fontWeight={600}
                                      color={theme.palette.darkGrey[700]}
                                      sx={{
                                        whiteSpace: 'normal',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 1,
                                        WebkitBoxOrient: 'vertical',
                                      }}
                                    >
                                      {player.player2_name}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            )}
                          </Box>
                          {createCells(
                            i,
                            group.length,
                            player,
                            group,
                            matchMap,
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            );
          })}
      </Box>
    </Box>
  );
};

export default GroupTable;
